import {faDiamondExclamation} from '@fortawesome/pro-light-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Backdrop,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {createContext, Fragment, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useActivityClickApi} from '../../../api/activityClick';
import {useAssessmentApi} from '../../../api/assessment';
import {useQuestionnaireApi} from '../../../api/demographics';
import {useNuralogixApi} from '../../../api/nuralogix';
import {useUserApi} from '../../../api/user';
import {consistentSpacing} from '../../../components/APIForms/variables';
import AdvancedScreeningDialog from '../../../components/dialogbox/advancedScreeningDialog';
import InformativeDialog from '../../../components/dialogbox/informativeDialog';
import {AnimationLoadingScreenWithProgressBar} from '../../../components/loading/loadingScreen';
import {PricingPlansPopup} from '../../../components/pricingOptions/pricingPlansPopup';
import CircularStaticProgress from '../../../components/progressBars/circularStaticProgressBar';
import MulticolorSlider from '../../../components/sliders/fiveColourSliderResults';
import ThreeColourSlider from '../../../components/sliders/threeColourSliderResults';
import {MetricCard} from '../../../components/vitalSigns/metricCard';
import {Symptom} from '../../../components/vitalSigns/symptom';
import {paymentsEnabled} from '../../../constants';
import APIObjectsContext from '../../../context/APIObjectsContext';
import MiscContext from '../../../context/MiscContext';
import {getResultLevelRanges} from '../../../nuralogix/resultLevels';
import {circularProgressGradient} from '../../../theme/gradients';
import {calculateAge, getThemeHexColor} from '../../../utils/miscellaneous';
import {SymptomsBase} from '../base';

export const ResponsiveContext = createContext();

/**
 * Returns a string or number with the appropiate way to display a metric based on the metric title.
 * This is because some metrics required to be rounded up to 2 decimal places, others one or other metrics
 * even receive multiple results and have to output that all at once
 *
 * @param result {Number[]} - Result to be formatted
 * @param metricTitle {string} - metric name, should match one of the titles provided in the finalResults.js lists for metrics
 *
 * @returns {Number | string} The appropiate way to display the metric based on the title
 */
export const handleResultDisplayByMetric = (result, metricTitle) => {
  if (metricTitle === 'Blood Pressure') return `${Number(result[0].toFixed(0))} / ${Number(result[1].toFixed(0))}`;

  if (metricTitle.match(/^(Heart Rate Variability)|(Mental Stress Index)$/)) return Number(result[0].toFixed(1));

  if (metricTitle.match(/^(Cardiac Workload)|(Vascular Capacity)$/)) return Number(result[0].toFixed(2));
  return Math.round(result[0]).toFixed(0);
};

const CustomBanner = ({toggleAccordionBanner, expanded}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isGalaxyFold = useMediaQuery('(max-width:280px)');

  return (
    <Box
      sx={{
        backgroundColor: '#F4F4F4',
        paddingBottom: !expanded ? 1 : 0,
        paddingLeft: 2,
        paddingRight: 2,
        paddingTop: 2,
        cursor: 'pointer',
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: 'rgba(217,217,217,0.10)',
        },
        transition: 'background-color 0.3s',
      }}
      onClick={toggleAccordionBanner}
    >
      {isSmallScreen ? (
        <>
          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
            <FontAwesomeIcon
              icon={faDiamondExclamation}
              size="xl"
              style={{
                color: '#35C6C9',
                marginRight: '20px',
                marginLeft: '8px',
                paddingTop: '4px',
                display: 'block',
                marginBottom: '4px',
              }}
            />
            <Typography variant={isGalaxyFold ? 'caption' : 'body2'} sx={{fontWeight: 'bold'}}>
              For Investigational Use Only
            </Typography>
          </Box>
          <Box sx={{display: 'flex', alignItems: 'flex-start', px: 1, mb: 1}}>
            <Typography variant="body2">
              Our services are not intended for medical purposes. The services do not offer any kind of medical advice
              and are not intended for any medical use or purpose. Our services are intended for general wellness only.
            </Typography>
          </Box>
        </>
      ) : (
        <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
          <FontAwesomeIcon
            icon={faDiamondExclamation}
            size="2xl"
            style={{
              color: '#35C6C9',
              marginRight: '20px',
              marginLeft: '8px',
              paddingTop: '4px',
              display: 'block',
              marginBottom: '4px',
            }}
          />
          <Typography variant="body2">
            <span style={{fontWeight: 'bold'}}>For Investigational Use Only.</span> Our services are not intended for
            medical purposes. The services do not offer any kind of medical advice and are not intended for any medical
            use or purpose. Our services are intended for general wellness only.
          </Typography>
        </Box>
      )}
      <Box sx={{display: 'flex', alignItems: 'flex-start', px: 1, pb: 1}}>
        <Collapse in={expanded}>
          <Typography variant="body2" sx={{pt: 1, mb: 2}}>
            The estimative measurements provided to you of physiological and psychological states including heart rate
            and stress should not be treated as medical advice. The measurements we provide to you are not intended to
            be utilized for any medical purposes or to diagnose, treat, cure or prevent any disease, ailment or injury.
            To the maximum extent permitted by applicable law, you expressly agree we are not providing any medical
            advice via our services. All content provided by our services including the measurements is not intended to
            be and should not be used in place of (a) the advice of your physician or other medical professionals, and
            (b) a visit, call or consultation with your physician or other medical professionals. Should you have any
            health-related questions, please see your physician or other healthcare provider promptly. If you have an
            emergency, call your physician or your local emergency services immediately. Your use of our services does
            not constitute or create a doctor-patient, therapist-patient or other healthcare professional relationship
            between AI-MD and you.
          </Typography>
          <Typography variant="body2" sx={{fontWeight: 'bold'}}>
            Accuracy
          </Typography>
          <Typography variant="body2">
            The measurements provided to you are based on indicators and standards developed by AI-MD and are only for
            personal information purposes, without any representations or warranties, express or implied. While we make
            every effort to provide accurate estimative measures, we make no warranty and disclaim all responsibility
            and liability for any accuracy, reliability, availability, security, completeness or correct use of
            measurements, content and any other information or results you receive through the services.
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
};

/**
 * React functional component representing the Final Results Page.
 * @component
 * @returns {JSX.Element} The rendered FinalResultsPage component.
 */
export const FinalResultsPage = () => {
  const {userAssessmentID, setUserAssessmentID} = useContext(APIObjectsContext);
  const {userPlan} = useContext(MiscContext);
  const navigate = useNavigate();

  // apis
  const nuralogixApi = useNuralogixApi();
  const assessmentApi = useAssessmentApi();

  const [isVitalSignsRecord, setIsVitalSignsRecord] = useState(null);
  const [vitalSigns, setVitalSigns] = useState([]);
  const [diseaseRisks, setDiseaseRisks] = useState([]);
  const [diabetesRisks, setDiabetesRisks] = useState([]);
  const [physicalHealth, setPhysicalHealth] = useState([]);
  const [overallHealthScore, setOverallHealthScore] = useState({});
  const [symptomCheckerResults, setSymptomCheckerResults] = useState([]);
  const [isSymptomCheckerPresent, setIsSymptomCheckerPresent] = useState(false);
  const [isLoading, setIsLoading] = useState({
    loadingNuralogix: true,
    loadingSymptomChecker: true,
  });
  const [responseReturned, setResponseReturned] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenMainBanner, setIsOpenMainBanner] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [nuraResultsDetailsDialog, setNuraResultsDetailsDialog] = useState({
    title: '',
    details: '',
    results: '',
    signInitials: '',
    isPremium: false,
  });

  const [informativeDialog, setInformativeDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
    width: 'sm',
  });

  const [advancedScreeningDialog, setAdvancedScreeningDialog] = useState({
    title: '',
    subTitle: `Upgrade to premium to record your results, track them over time,
    get access to advanced AI-driven symptom screening and more!`,
    isOpen: false, //to display popup, set isOpen to true (this is until proper functionality is build)
    width: 'sm',
  });

  const [apiResults, setApiResults] = useState({
    isFaceScan: false,
    isSymptomChecker: false,
  });

  // this is to get user age
  const [demographicsData, setDemographicsData] = useState({});
  const questionnaireApi = useQuestionnaireApi();
  const activityClickApi = useActivityClickApi();
  const [mustChangeLayout, setMustChangeLayout] = useState(false);
  const [paymentsPopupOpen, setPaymentsPopupOpen] = useState(false);

  const verticalGap = 2;
  const theme = useTheme();

  const isMediumScreen = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isGalaxyFold = useMediaQuery('(max-width:280px)');

  useEffect(() => {
    const fetchData = async () => {
      //get user demographics to extract age
      const demographics = await questionnaireApi.getQuestionnaire();
      if (demographics.status === 200) setDemographicsData(demographics.data);

      //get user assessment id
      const assessment = await assessmentApi.getLatestAssessment();
      if (assessment.status === 200) {
        setUserAssessmentID(assessment.data.id);
        if (assessment.data.symptoms_description !== null) {
          setIsSymptomCheckerPresent(true);

          // get assessment results only if the latest assessment is a symptom input
          // by the user (the assessment description should be different than None)
          const assessmentResults = await assessmentApi.getResultsAssessment(assessment.data.id);
          if (assessmentResults.status === 200) {
            const symptomsList = assessmentResults.data.conditions.map((condition, index) => {
              return {
                id: index,
                name: condition.name,
                probability: condition.probability,
                symptomColour: getThemeHexColor(),
              };
            });

            // Sort the symptomsList array based on probability in descending order
            symptomsList.sort((a, b) => b.probability - a.probability);

            // Get the top 3 items from the sorted array
            const top3Symptoms = symptomsList.slice(0, 3);

            setSymptomCheckerResults(top3Symptoms);
            setApiResults(prev => {
              return {
                ...prev,
                isSymptomChecker: true,
              };
            });
          } else {
            setIsSymptomCheckerPresent(false);
          }
        }
      }

      //Make loading spinner go away (false) only if user go through dermadetect questions
      setResponseReturned(true);
      setIsLoading({
        loadingNuralogix: false,
        loadingSymptomChecker: false,
      });
    };
    fetchData();
  }, []);

  //Gets nuralogix results from specific assessment id
  useEffect(() => {
    const fetchData = async () => {
      if (userAssessmentID == undefined) return;

      let resultsVitals;
      try {
        resultsVitals = await nuralogixApi.getNuralogixResultsVitals(userAssessmentID);
        setApiResults(prev => {
          return {
            ...prev,
            isFaceScan: true,
          };
        });
      } catch {
        setResponseReturned(true);
        setIsLoading({
          loadingNuralogix: false,
          loadingSymptomChecker: isSymptomCheckerPresent,
        });
        // Sets isFaceScan to false when there is a 404 (Not Found) response from server to handle
        // message in case there are no results to show
        return;
      }
      const res = resultsVitals;

      // state to keep track when to hide vital signs related text (titles, etc.)
      setIsVitalSignsRecord(Object.keys(res.data).length > 0 ? true : false);

      const vitalSignsList = [
        {
          key: 'bloodPressure',
          title: 'Blood Pressure',
          vitalStatus: 'Normal',
          vitalSignInitials: 'mmHg',
          result: [res.data.blood_pressure_systolic, res.data.blood_pressure_diastolic],
          vitalSymbolVal: 1,
          display: true,
          isPremium: false,
          details:
            'Blood pressure is the force of blood against the walls of the arteries as the heart pumps blood throughout the body. It is expressed as two numbers, the systolic pressure and the diastolic pressure, measured in millimeters of mercury (mmHg).//The systolic pressure measures the force of blood as the heart contracts and pumps blood out of the heart, while the diastolic pressure measures the force of blood when the heart is at rest between beats.//High blood pressure (hypertension) can be a risk factor for heart disease, stroke, and other health problems. Low blood pressure (hypotension) can cause dizziness, fainting, and other symptoms. Blood pressure can be influenced by various factors such as age, genetics, weight, diet, exercise, stress, and underlying health conditions. Regular monitoring of blood pressure can be a useful tool for maintaining good cardiovascular health and preventing health problems associated with high or low blood pressure.',
        },
        {
          key: 'breathingRate',
          title: 'Breathing Rate',
          vitalStatus: 'Normal',
          vitalSignInitials: 'brpm',
          result: [res.data.respiration],
          vitalSymbolVal: 6,
          display: true,
          isPremium: false,
          details:
            "Breathing rate is the number of times you breathe in and out per minute. It's a measurement of how well your body is taking in oxygen and getting rid of carbon dioxide. When you inhale, your lungs take in oxygen, which is carried by the blood to the body's tissues. When you exhale, your lungs get rid of carbon dioxide, which is produced by the body's cells.//Breathing rate can be influenced by various factors such as exercise, stress, illness, and overall health. Monitoring respiration rate can be a useful tool in assessing respiratory function and overall health.//A slower breathing rate may be caused by certain medications or suggest an underlying medical condition, for example, hypothyroidism, heart condition, electrolyte imbalance, etc. A faster breathing rate could have many causes including walking fast or exercise, asthma, anxiety, infection, or cardiovascular condition. You may want to consult your doctor if your breathing rate falls outside of the normal range.//The normal resting breathing rate for adults is between 12 and 25 breaths per minute.",
        },
        {
          key: 'pulseRate',
          title: 'Pulse Rate',
          vitalStatus: 'Normal',
          vitalSignInitials: 'bpm',
          result: [res.data.pulse_rate],
          vitalSymbolVal: 17,
          display: true,
          isPremium: false,
          details:
            "HR_BPM is the DeepAffex Cloud's estimate of the subject's Pulse Rate. It is based on analyzing facial blood-flow data (TOI).//Pulse Rate is the number of times the user's heart beats, expressed as a rate per minute.",
        },
        {
          key: 'heartRateVariability',
          title: 'Heart Rate Variability',
          vitalStatus: 'Normal',
          vitalSignInitials: 'ms',
          result: [res.data.heart_rate_change],
          vitalSymbolVal: 2,
          display: true,
          isPremium: false,
          details:
            'Heart Rate Variability refers to the variability in the timing between one heartbeat and the next. Anura™ measures this with SDNN, which is the standard deviation of the interval between normal heartbeats (in milliseconds). A higher SDNN corresponds to more inter-beat variability.Increased heart rate variability suggests an increase in parasympathetic activity and/or a decrease in sympathetic activity of the autonomic nervous system.//High resting heart rate variability is considered healthy and means that the heart is more flexible to the changing needs of the body.',
        },
        {
          key: 'cardiacWorkload',
          title: 'Cardiac Workload',
          vitalStatus: 'Normal',
          vitalSignInitials: 'dB',
          result: [res.data.cardiac_workload],
          vitalSymbolVal: 4,
          display: true,
          isPremium: false,
          details:
            'Cardiac Workload, or more precisely myocardial workload, is a measure of the stress put on the heart muscle.//Cardiac Workload can be calculated using the formula: Heart Rate x Systolic Blood Pressure. When measured at rest, this index can be used as an indicator of cardiovascular health.//For example, a person who exercises regularly may have lower Cardiac Workload than another person who has a sedentary lifestyle. During intense physical exercise, your Cardiac Workload may increase but should decrease post-exercise.',
        },
        {
          key: 'vascularCapacity',
          title: 'Vascular Capacity',
          vitalStatus: 'Normal',
          vitalSignInitials: 'seconds',
          result: [res.data.vascular_capacity],
          vitalSymbolVal: 5,
          display: true,
          isPremium: false,
          details:
            "Vascular Capacity or Tau, is a measure of the elasticity of subject's blood vessels.//When measured at rest, this index can be used as an indicator of cardiovascular health as it is strongly correlated with vascular stiffness.//A person with a high Tau has better vascular health compared to a person with a low Tau. Also, certain transient activities and physiological events can lead to immediate changes in Tau (e.g., drinking alcohol, smoking).",
        },
      ];

      const diseaseRiskList = [
        {
          key: 'heartAttackRisk',
          title: 'Heart Attack Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.heart_disease_risk],
          display: !demographicsData.has_had_heart_attack,
          isPremium: true,
          details:
            "Heart Attack Risk is your likelihood of experiencing your first heart attack within the next 10 years, expressed as a percentage.//This risk score considers your gender, age, Body Mass Index, systolic blood pressure and antihypertensive medication status, as well as your smoking status and diabetes status when available. It was developed using the Framingham method. It is based on data from prospective studies that followed participants' cardiovascular health for over 10 years.",
        },
        {
          key: 'cardiovascularDiseaseRisk',
          title: 'Cardiovascular Disease Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.cardiovascular_risk],
          display: true,
          isPremium: true,
          details:
            "Cardiovascular Disease Risk is your likelihood of experiencing your first heart attack or stroke within the next 10 years, expressed as a percentage.//This risk score considers your gender, age, Body Mass Index, systolic blood pressure and antihypertensive medication status, as well as your smoking status and diabetes status when available. It was developed using the Framingham method. It is based on data from prospective studies that followed participants' cardiovascular health for over 10 years.",
        },
        {
          key: 'strokeRisk',
          title: 'Stroke Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.stroke_risk],
          display: !demographicsData.has_had_stroke,
          isPremium: true,
          details:
            "Stroke Risk is your likelihood of experiencing your first stroke within the next 10 years, expressed as a percentage.//This risk score considers your gender, age, Body Mass Index, systolic blood pressure and antihypertensive medication status, as well as your smoking status and diabetes status when available. It was developed using the Framingham method. It is based on data from prospective studies that followed participants' cardiovascular health for over 10 years.",
        },
        {
          key: 'hypertriglyceridemiaRisk',
          title: 'Hypertriglyceridemia Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.hypertriglyceridemia_risk],
          display: true,
          isPremium: true,
          details:
            "Hypertriglyceridemia risk corresponds to the percentage of people with the user's risk profile who have abnormally high triglyceride levels (above 1.7 mmol/L or 150 mg/dL).//This risk profile is based on their facial blood flow and demographic information.//Hypertriglyceridemia in combination with other factors is a treatable risk factor for cardiovascular disease. Therefore, those with a high hypertriglyceridemia risk should talk to their doctor about having their triglyceride levels checked with a blood test.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements throughout the day and across several days to adequately account for this physiological variation.",
        },
        {
          key: 'hypercholesterolemiaRisk',
          title: 'Hypercholesterolemia Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.hypercholesteremia_risk],
          display: true,
          isPremium: true,
          details:
            "Hypercholesterolemia risk corresponds to the percentage of people with the user's risk profile who have abnormally high cholesterol levels (defined as a total cholesterol (TC)-to-high density lipoprotein (HDL) cholesterol (“good cholesterol”) ratio of 4.3 or higher).//This risk profile is based on their facial blood flow and demographic information.//Hypercholesterolemia is a treatable risk factor for cardiovascular disease. Therefore, those with a high hypercholesterolemia risk should talk to their doctor about having their HDL and total cholesterol levels checked with a blood test.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements throughout the day and across several days to adequately account for this physiological variation.",
        },
        {
          key: 'hypertensionRisk',
          title: 'Hypertension Risk',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.hypertension_risk],
          display: demographicsData.has_high_bp ? false : true,
          isPremium: true,
          details:
            "Hypertension risk corresponds to the percentage of people with the user's risk profile who are diagnosed with hypertension (high blood pressure) by their doctor.//This risk profile is based on their facial blood flow and demographic information.//Hypertension is a major treatable risk factor for cardiovascular disease. Therefore, those with a high hypertension risk should consider getting screened for hypertension by their doctor.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements throughout the day and across several days to adequately account for this physiological variation.",
        },
      ];

      const diabetesRiskList = [
        {
          key: 'HbA1c',
          title: 'Risk of HbA1c',
          smallerText: '> 5.7%',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.hba1c],
          display: true,
          isPremium: true,
          details:
            "HbA1c risk corresponds to the percentage of people with the user's risk profile who had their HbA1c levels above 5.7% when tested, indicating an elevated risk of prediabetes.//Their risk profile is based on facial blood flow and demographics.//HbA1c or glycated hemoglobin is formed when glucose from the blood stream combines with hemoglobin in red blood cells. The HbA1c test is a laboratory blood test ordered by your health care provider. It estimates the average blood glucose level during the past 2-3 months by measuring the amount of glycated hemoglobin A1c in a person’s blood. The higher the A1c percentage, the higher the blood glucose levels, thus helping in the diagnosis of type 2 diabetes. There are other conditions and physiological states beyond just diabetes which may alter HbA1c values. Therefore, it is important to discuss the meaning of your readings with your health care provider and determine if further assessment is required.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements throughout the day and across several days to adequately account for this physiological variation.",
        },
        {
          key: 'fastingBloodGlucose',
          title: 'Risk of Fasting Blood Glucose',
          smallerText: '> 5.5 mmol/L',
          vitalStatus: 'Normal',
          vitalSignInitials: '%',
          result: [res.data.blood_glucose],
          display: true,
          isPremium: true,
          details:
            "Fasting blood glucose risk corresponds to the percentage of people with the user's risk profile who had their blood glucose levels above 5.5 mmol/L when tested after 8-10 hours of fasting, indicating a high risk of prediabetes.//Their risk profile is based on facial blood flow and demographics.//Fasting blood glucose (FBG) test is a laboratory blood test ordered by your health care provider, performed after 8-10 hrs of fasting. This blood test is the most accurate way to determine a blood glucose result. FBG may also be determined using over the counter glucometers. These glucometers may produce FBG results that vary in accuracy by as much as +-15%. There are other conditions and physiological states beyond just diabetes which can alter FBG results.//Therefore, it is important to discuss the meaning of your readings with your health care provider and determine if further assessment is required.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements across several days to adequately account for this physiological variation.",
        },
        {
          key: 'type2DiabetesRisk',
          title: 'Type II Diabetes Risk',
          vitalStatus: 'Higher than average',
          vitalSignInitials: '%',
          result: [res.data.diabetes_risk],
          display: demographicsData.diabetes_type_id === 3 ? true : false,
          isPremium: true,
          details:
            "Type II diabetes risk corresponds to the percentage of people with the user's risk profile who are diagnosed with type 2 diabetes (impaired processing of blood sugar) by their doctor.//This risk profile is based on their facial blood flow and demographic information. It does not necessarily reflect the user's current blood sugar level.//Uncontrolled type 2 diabetes can cause problems like vascular disease, heart disease, kidney damage, and nerve damage. Therefore, those with a high type 2 diabetes risk should consider getting screened for type 2 diabetes by their doctor.//Notice: Moment-to-moment fluctuations in your physiology mean that your risk estimates could vary to some degree from one measurement to the next. The best estimate of your overall risk is therefore obtained by averaging several measurements throughout the day and across several days to adequately account for this physiological variation.",
        },
      ];

      const physicalHealthList = [
        {
          key: 'facialSkinAge',
          title: 'Facial Skin Age',
          vitalStatus: 'Normal',
          vitalSignInitials: '',
          result: [res.data.face_skin_age],
          vitalSymbolVal: 3,
          display: true,
          isPremium: true,
          details:
            'Facial skin age is an estimate of your age based on the condition of the surface of your face. This estimation can be used as an indicator for your facial skin aging.//This estimation may be influenced by many factors such as fatigue level, the use of skincare or cosmetics products.//Video images taken in poor lighting conditions (e.g., backlighting, overhead lighting) may distort the estimation of your facial skin age.',
        },
        {
          key: 'mentalStressIndex',
          title: 'Mental Stress Index',
          vitalStatus: 'Higher than average',
          vitalSignInitials: '',
          result: [res.data.stress_index],
          vitalSymbolVal: 9,
          display: true,
          isPremium: true,
          details:
            'Mental Stress is the change in the internal homeostasis caused by a psychological, physiological, or environmental stressor. Stimuli that cause stress result in the activation of sympathetic nervous system rendering a chain of physiological changes.',
        },
        {
          key: 'bodyMassIndex',
          title: 'Body Mass Index',
          vitalStatus: 'Normal',
          vitalSignInitials: (
            <span>
              kg/m<sup>2</sup>
            </span>
          ),
          result: [res.data.body_mass_index],
          vitalSymbolVal: 7,
          display: true,
          isPremium: false,
          details:
            'Body Mass Index (BMI) is a measure of an individuals tissue mass (muscle, fat, and bone) adjusted for height. It is a commonly used indicator of overall body fat and serves as a tool for categorizing individuals as being underweight, normal weight, overweight, or obese on the basis of health risk.//Body Mass Index is a fair predictor of cardiovascular diseases, type 2 diabetes, gallstones, and certain cancers. It is the one of the best anthropometric predictors of high-density lipoprotein (HDL) and triglyceride levels; these are important factors for cardio-metabolic diseases. It is also one of the best anthropometric predictors of uric acid levels (high uric acid levels contribute to gallstones). A limitation of Body Mass Index is that it cannot distinguish between fat and muscle mass and thus can be inaccurate in muscular individuals.//You can calculate your Body Mass Index using the formula: BMI = weight (kg) / height (m²)',
        },
      ];

      const overallHealth = {
        title: 'Overall Health Score',
        result: res.data.overall_wellness,
        details:
          'Overall Health Score is a measure of your overall physiological health based on a combined calculation of your most essential vitals. The higher your Overall Health Score, the better your health.',
      };

      setVitalSigns(vitalSignsList);
      setDiseaseRisks(diseaseRiskList);
      setDiabetesRisks(diabetesRiskList);
      setOverallHealthScore(overallHealth);
      setPhysicalHealth(physicalHealthList);

      setResponseReturned(true);
      setIsLoading({
        loadingNuralogix: false,
        loadingSymptomChecker: isSymptomCheckerPresent,
      });
    };

    fetchData();
  }, [userAssessmentID, demographicsData]);

  const handleCancel = () => {
    navigate('/symptom-checker');
  };

  const handleNext = () => {
    navigate('/thank-you');
  };

  const handleUpgradePremiumClick = async () => {
    //Save in DB click activity of corresponding button
    activityClickApi.postActivityClick('premium_link');

    //open modal popup (this for first released which does not include premium version)
    if (!paymentsEnabled) {
      setInformativeDialog({
        title: 'Coming soon',
        subTitle: `Our premium version has not been released, we'll update you when it is!`,
        isOpen: true,
        width: 'sm',
        onConfirm: () => {
          setInformativeDialog(prev => {
            return {...prev, isOpen: false};
          });
        },
      });
      return;
    }
    setPaymentsPopupOpen(true);
  };

  const actualAge = calculateAge(demographicsData.date_of_birth);

  // Function to handle click and open the modal for banner in main results page
  const closeModalMainBanner = isClose => {
    setIsOpenMainBanner(isClose);
  };

  //Handles action to open Main banner modal dialog
  const handleModalMainBanner = () => {
    setIsOpenMainBanner(true);
  };

  // Function to handle click and open the modal popup for a metric
  const handleClickModal = measurement => {
    setIsOpen(true);

    const paragraphs = measurement.details.split('//').map((paragraph, index) => <p key={index}>{paragraph}</p>);

    const {nuralogixLevel, colourCircleSlider, colourLineRanges, circlePosition} = getResultLevelRanges(
      measurement.result,
      measurement.title,
      true,
      actualAge,
    );

    setNuraResultsDetailsDialog({
      title: measurement.title,
      details: <div>{paragraphs}</div>,
      results: measurement.result,
      signInitials: measurement.vitalSignInitials,
      level: nuralogixLevel,
      colour: colourCircleSlider,
      isPremium: true,
    });
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  //Handles banner in modal popup when banner is clicked
  const toggleAccordion = () => {
    setExpanded(!expanded);
  };

  const handleSymptomCheckerRedirect = () => {
    navigate('/symptom-checker');
  };

  const allListsData = [
    {
      key: 'vitalSigns',
      title: 'Vital Signs',
      list: vitalSigns,
    },
    {
      key: 'physicalHealth',
      title: 'Physical Health',
      list: physicalHealth,
    },
    {
      key: 'diabetesRisk',
      title: 'Diabetes Risk',
      list: diabetesRisks,
    },
    {
      key: 'generalDiseaseRisk',
      title: 'General Disease Risk',
      list: diseaseRisks,
    },
  ];

  //Blood Pressure metrics
  const bloodPressure = [
    {
      id: 0,
      title: 'Systolic Blood Pressure',
    },
    {
      id: 1,
      title: 'Diastolic Blood Pressure',
    },
  ];

  return (
    <>
      {isLoading.loadingNuralogix && isLoading.loadingSymptomChecker ? (
        <AnimationLoadingScreenWithProgressBar
          title="Loading..."
          secondsToLoad={30}
          isAlmostComplete={responseReturned}
        />
      ) : (
        <>
          <SymptomsBase
            pageNumber={2}
            handleBack={() => handleCancel()}
            handleNext={() => handleNext()}
            backButtonName={'Back'}
            nextButtonName={'Next'}
            dontAnimate={paymentsPopupOpen}
          >
            <Stack>
              {apiResults.isFaceScan && apiResults.isSymptomChecker && (
                <Typography variant="h4" textAlign="center" sx={{mt: 4}}>
                  Results
                </Typography>
              )}
              <Grid
                container
                alignItems="stretch"
                sx={{
                  marginTop: 2,
                  marginBottom: 1,
                  justifyContent: isMediumScreen ? 'space-between' : 'start',
                  alignItems: 'stretch',
                  rowGap: {
                    xs: 2,
                    md: 6,
                  },
                  columnGap: verticalGap,
                }}
                columns={{
                  xs: 10,
                  sm: 16, //important to preserve this layout so the results grid shows properly
                  md: 19,
                }}
              >
                {!isSmallScreen && isVitalSignsRecord && (
                  <Grid item xs={12} sm={1} md={1} lg={1} sx={{mt: 1}}>
                    <CircularStaticProgress
                      value={overallHealthScore.result}
                      color={circularProgressGradient}
                      width={!isSmallScreen ? '75px' : '40px'}
                      height={!isSmallScreen ? '75px' : '40px'}
                      size={!isSmallScreen ? 75 : 40}
                      thickness={4}
                      metricName={overallHealthScore.title}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={13} md={15} lg={16} sx={{ml: isSmallScreen ? 0 : 3}}>
                  <Typography variant="metricGroupTitle">{overallHealthScore.title}</Typography>
                  <Typography
                    gutterBottom
                    variant="subtitle1"
                    component="div"
                    textAlign={isSmallScreen ? 'justify' : undefined}
                  >
                    {overallHealthScore.details}
                  </Typography>
                </Grid>
                {isSmallScreen && isVitalSignsRecord && (
                  <Grid container item xs={12} sm={1} md={1} justifyContent="center" alignItems="center">
                    <Box alignItems="center" justifyContent="center">
                      <CircularStaticProgress
                        value={overallHealthScore.result}
                        color={circularProgressGradient}
                        width="80px"
                        height="80px"
                        size={80}
                        thickness={4}
                        metricName={overallHealthScore.title}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
              {isSymptomCheckerPresent ? (
                <>
                  <Typography variant="metricGroupTitle" sx={{mb: 0, mt: 3}}>
                    Symptom Checker
                  </Typography>
                  <Typography variant="body2" textAlign={'justify'}>
                    According to our symptom checker tool, your symptoms most closely match the following conditions:
                  </Typography>
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: {
                        xs: 'start',
                        sm: symptomCheckerResults.length <= 2 ? 'start' : 'space-between',
                      },
                      my: {
                        xs: 0,
                        sm: 1,
                        md: 2,
                      },
                      marginLeft: '0 !important',
                      columnGap: verticalGap,
                    }}
                    columns={{
                      xs: 3,
                      sm: 9,
                      md: 20,
                    }}
                  >
                    {symptomCheckerResults.map(
                      (
                        item, //adjust this part to integrate new implementation
                      ) => (
                        <Grid
                          item
                          xs={3}
                          sm={4}
                          md={6}
                          lg={8}
                          key={item.id}
                          sx={{
                            mt: 1,
                            mr: {
                              xs: 0,
                              sm: symptomCheckerResults.length === 2 ? 4 : 0,
                            },
                          }}
                        >
                          <Symptom
                            key={item.id}
                            name={item.name}
                            result={item.probability}
                            symptomColour={item.symptomColour}
                          />
                        </Grid>
                      ),
                    )}
                  </Grid>
                </>
              ) : null}
              {isVitalSignsRecord
                ? allListsData.map((data, idx) => {
                    return [
                      <Typography key={`${idx}${data.key}typography`} variant="metricGroupTitle" sx={{mt: 5, mb: 0}}>
                        {data.title}
                      </Typography>,
                      <Grid
                        key={`${idx}${data.key}grid`}
                        container
                        sx={{
                          marginTop: 2,
                          marginBottom: 1,
                          justifyContent: 'stretch',
                          alignItems: 'stretch',
                          rowGap: {
                            xs: 2,
                            md: 6,
                          },
                          columnGap: verticalGap,
                        }}
                      >
                        <ResponsiveContext.Provider value={{mustChangeLayout, setMustChangeLayout}}>
                          {data.list.map((item, index) => {
                            return (
                              item.display &&
                              !item.result.includes(null) && (
                                <Grid item xs={12} md={5.8} lg={3.8} key={index}>
                                  <MetricCard
                                    title={item.title}
                                    vitalStatus={getResultLevelRanges(item.result, item.title, false, actualAge)}
                                    vitalSignInitials={item.vitalSignInitials}
                                    result={item.result}
                                    isPremium={item.isPremium}
                                    vitalSymbolVal={item.vitalSymbolVal}
                                    handleClick={() => {
                                      handleClickModal(item);
                                    }}
                                    plan={userPlan}
                                    smallerText={item.smallerText === undefined ? undefined : item.smallerText}
                                  />
                                </Grid>
                              )
                            );
                          })}
                        </ResponsiveContext.Provider>
                      </Grid>,
                    ];
                  })
                : ''}
              {userPlan === 'basic' && (apiResults.isFaceScan || apiResults.isSymptomChecker) && (
                <Typography variant="body2" sx={{my: 3, textAlign: 'justify'}}>
                  Learn which condition is the most probable, how you rank with people in similar age and gender, and
                  track your symptom progress by{' '}
                  <Button
                    variant="text"
                    onClick={handleUpgradePremiumClick}
                    sx={{
                      color: '#3da9f8',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    upgrading to premium
                  </Button>
                </Typography>
              )}
              {actualAge < 13 && !apiResults.isFaceScan && !apiResults.isSymptomChecker && (
                <Grid container rowGap={consistentSpacing} sx={{width: '100%', mb: 4}}>
                  <Grid item xs={12}>
                    <Typography mt={3} sx={{textAlign: 'center'}}>
                      There are no results to present because the face scan technology is not available for children
                      (yet!).
                      <br />
                      We will notify you when this functionality becomes available.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sx={{textAlign: 'center'}}>
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{
                        color: 'white',
                        background: 'primary',
                        borderRadius: 5,
                      }}
                      onClick={handleSymptomCheckerRedirect}
                      style={{
                        textDecoration: 'none',
                        textTransform: 'none',
                      }}
                    >
                      Try the assessment process again
                    </Button>
                  </Grid>
                </Grid>
              )}
              {actualAge < 13 && apiResults.isSymptomChecker && !apiResults.isFaceScan && (
                <Typography variant="body2" sx={{mt: userPlan === 'premium' ? 3 : 0, mb: 3}}>
                  The face scan technology is not yet available for children. We will notify you when this becomes
                  available.
                </Typography>
              )}
              {(apiResults.isFaceScan || apiResults.isSymptomChecker) && (
                <ButtonBase
                  component="div"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    cursor: 'pointer',
                    my: userPlan === 'premium' ? 3 : 0,
                  }}
                  onClick={handleModalMainBanner}
                >
                  {isSmallScreen ? (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <FontAwesomeIcon
                          icon={faDiamondExclamation}
                          size={'lg'}
                          style={{
                            color: '#35C6C9',
                            marginRight: '10px',
                            marginLeft: '0px',
                            paddingTop: '4px',
                            display: 'block',
                            marginBottom: '4px',
                          }}
                        />
                        <Typography variant={isGalaxyFold ? 'caption' : 'body2'} sx={{fontWeight: 'bold'}}>
                          For Investigational Use Only
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          px: isSmallScreen ? 0 : 1,
                        }}
                      >
                        <Typography variant="body2" sx={{textAlign: 'justify'}}>
                          Our services are not intended for medical purposes. The services do not offer any kind of
                          medical advice and are not intended for any medical use or purpose. Our services are intended
                          for general wellness only.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '4px',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faDiamondExclamation}
                        size="2xl"
                        style={{
                          color: '#35C6C9',
                          marginRight: '12px',
                          paddingTop: '4px',
                        }}
                      />
                      <Typography variant="body2">
                        <span style={{fontWeight: 'bold'}}>For Investigational Use Only.</span> Our services are not
                        intended for medical purposes. The services do not offer any kind of medical advice and are not
                        intended for any medical use or purpose. Our services are intended for general wellness only.
                      </Typography>
                    </Box>
                  )}
                </ButtonBase>
              )}
            </Stack>
          </SymptomsBase>
          <PricingPlansPopup open={paymentsPopupOpen} setOpen={setPaymentsPopupOpen} />
          {/* Modal dialog box when user clicks upgrading to premium link */}
          <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
          {/* Modal box when user clicks banner in main results page */}
          <Dialog
            open={isOpenMainBanner}
            maxWidth="md"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
            PaperProps={{
              sx: {
                borderRadius: '30px',
                position: 'absolute',
                mx: {
                  xs: 2,
                  md: 'auto',
                },
              },
            }}
            sx={{
              top: theme.spacing(5),
              pt: theme.spacing(2),
              pb: theme.spacing(1),
            }}
          >
            <DialogTitle sx={{paddingY: theme.spacing(2)}}>
              <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                <FontAwesomeIcon
                  icon={faDiamondExclamation}
                  size="lg"
                  style={{
                    color: '#35C6C9',
                    marginRight: '20px',
                    marginLeft: '8px',
                    paddingTop: '4px',
                    display: 'block',
                  }}
                />
                <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                  For Investigational Use Only
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={{
                paddingRight: theme.spacing(3),
              }}
            >
              <Typography variant="body2" sx={{mb: 2}}>
                Our services are not intended for medical purposes. The services do not offer any kind of medical advice
                and are not intended for any medical use or purpose. Our services are intended for general wellness
                only.
              </Typography>
              <Typography variant="body2" sx={{mb: 2}}>
                {' '}
                The estimative measurements provided to you of physiological and psychological states including heart
                rate and stress should not be treated as medical advice. The measurements we provide to you are not
                intended to be utilized for any medical purposes or to diagnose, treat, cure or prevent any disease,
                ailment or injury. To the maximum extent permitted by applicable law, you expressly agree we are not
                providing any medical advice via our services. All content provided by our services including the
                measurements is not intended to be and should not be used in place of (a) the advice of your physician
                or other medical professionals, and (b) a visit, call or consultation with your physician or other
                medical professionals. Should you have any health-related questions, please see your physician or other
                healthcare provider promptly. If you have an emergency, call your physician or your local emergency
                services immediately. Your use of our services does not constitute or create a doctor-patient,
                therapist-patient or other healthcare professional relationship between AI-MD and you.
              </Typography>

              <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
                Accuracy
              </Typography>
              <Typography variant="body2">
                The measurements provided to you are based on indicators and standards developed by AI-MD and are only
                for personal information purposes, without any representations or warranties, express or implied. While
                we make every effort to provide accurate estimative measures, we make no warranty and disclaim all
                responsibility and liability for any accuracy, reliability, availability, security, completeness or
                correct use of measurements, content and any other information or results you receive through the
                services.
              </Typography>
            </DialogContent>
            <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
              <Button variant="text" onClick={() => closeModalMainBanner(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal box when user clicks a nuralogix results box */}
          {userPlan === 'premium' || !nuraResultsDetailsDialog.isPremium ? (
            <Dialog
              open={isOpen}
              maxWidth="md"
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500,
              }}
              PaperProps={{
                sx: {
                  borderRadius: '30px',
                  position: 'absolute',
                  mx: {
                    xs: 2,
                    md: 'auto',
                  },
                },
              }}
              sx={{
                top: theme.spacing(5),
                pt: theme.spacing(2),
                pb: theme.spacing(1),
              }}
            >
              <DialogTitle sx={{pt: theme.spacing(3), pb: theme.spacing(0)}}>
                <Typography component="span" variant="h6">
                  {nuraResultsDetailsDialog.title}
                </Typography>
              </DialogTitle>
              <DialogContent
                sx={{
                  paddingY: theme.spacing(5),
                  paddingRight: theme.spacing(3),
                }}
              >
                <Grid
                  container
                  justifyContent="space-between"
                  height="fit-content"
                  spacing={1}
                  sx={{paddingTop: 1, pr: 1}}
                  columns={20}
                  alignItems={'top'}
                >
                  {(() => {
                    if (nuraResultsDetailsDialog.title === 'Blood Pressure')
                      return (
                        <>
                          {bloodPressure.map((bp, idx) => (
                            <Fragment key={idx}>
                              {isGalaxyFold ? (
                                <>
                                  <Grid item xs={19} key={bp.id}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 'bold',
                                        fontSize: '10px',
                                        mr: 2,
                                      }}
                                    >
                                      {bp.title}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={19} key={bp.id}>
                                    <Typography variant={'subtitle2'} sx={{fontWeight: 'bold'}}>
                                      {Number(nuraResultsDetailsDialog.results[bp.id].toFixed(0))}{' '}
                                      <span style={{fontSize: '10px'}}>{nuraResultsDetailsDialog.signInitials}</span>
                                    </Typography>
                                  </Grid>
                                </>
                              ) : (
                                <>
                                  <Grid
                                    item
                                    xs={19}
                                    //esto
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                    }}
                                    key={bp.id}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 'bold',
                                        //esto
                                        fontSize: isMediumScreen ? '12px' : '14px',
                                        mr: 2,
                                      }}
                                    >
                                      {bp.title}
                                    </Typography>
                                    <Typography
                                      variant={isMediumScreen ? 'body2' : 'subtitle1'}
                                      sx={{
                                        fontWeight: 'bold',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {Number(nuraResultsDetailsDialog.results[bp.id].toFixed(0))}{' '}
                                      <span style={{fontSize: '10px'}}>{nuraResultsDetailsDialog.signInitials}</span>
                                    </Typography>
                                  </Grid>
                                </>
                              )}
                              <Grid item xs={19} sm={9} md={10} lg={9}>
                                <MulticolorSlider
                                  result={nuraResultsDetailsDialog.results[bp.id]}
                                  metricName={bp.title}
                                />
                              </Grid>
                            </Fragment>
                          ))}
                        </>
                      );

                    if (nuraResultsDetailsDialog.title === 'Facial Skin Age')
                      return (
                        <Grid item xs={20} sm={12} md={5}>
                          <CircularStaticProgress
                            value={nuraResultsDetailsDialog.results[0]}
                            color={nuraResultsDetailsDialog.colour}
                            width="80px"
                            height="80px"
                            size={80}
                            thickness={4}
                            metricName={nuraResultsDetailsDialog.title}
                          />
                          <Typography
                            variant={isSmallScreen ? 'body2' : 'subtitle1'}
                            color={nuraResultsDetailsDialog.colour}
                            sx={{mt: 1}}
                          >
                            {nuraResultsDetailsDialog.level}{' '}
                            {nuraResultsDetailsDialog.level === 'Same' ? ' as ' : 'than '}
                            actual age
                          </Typography>
                        </Grid>
                      );

                    if (nuraResultsDetailsDialog.title.match(/^(Breathing Rate)|(Pulse Rate)$/))
                      return (
                        <>
                          {isSmallScreen ? (
                            <>
                              <Grid
                                item
                                xs={19}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                }}
                              >
                                <Typography variant={isMediumScreen ? 'body2' : 'subtitle1'} sx={{fontWeight: 'bold'}}>
                                  {Math.round(nuraResultsDetailsDialog.results[0]).toFixed(0)}
                                </Typography>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    marginLeft: '5px',
                                  }}
                                >
                                  {nuraResultsDetailsDialog.signInitials}
                                </span>
                              </Grid>
                              <Grid item xs={19} sm={9} md={10} lg={9}>
                                <ThreeColourSlider
                                  result={nuraResultsDetailsDialog.results[0]}
                                  metricName={nuraResultsDetailsDialog.title}
                                />
                              </Grid>
                            </>
                          ) : (
                            <Grid
                              item
                              xs={4}
                              sm={6}
                              md={10}
                              sx={{
                                padding: '0 !important',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'top',
                                alignItems: 'center',
                                ml: 1,
                              }}
                            >
                              <ThreeColourSlider
                                result={nuraResultsDetailsDialog.results[0]}
                                metricName={nuraResultsDetailsDialog.title}
                              />
                              <Typography
                                variant={isMediumScreen ? 'body2' : 'subtitle1'}
                                sx={{fontWeight: 'bold', ml: 2}}
                              >
                                {Math.round(nuraResultsDetailsDialog.results[0]).toFixed(0)}
                              </Typography>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  marginLeft: '5px',
                                }}
                              >
                                {nuraResultsDetailsDialog.signInitials}
                              </span>
                            </Grid>
                          )}
                        </>
                      );

                    return (
                      <>
                        {isSmallScreen ? (
                          <>
                            <Grid
                              item
                              xs={19}
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                              }}
                            >
                              <Typography variant={isMediumScreen ? 'body2' : 'subtitle1'} sx={{fontWeight: 'bold'}}>
                                {handleResultDisplayByMetric(
                                  nuraResultsDetailsDialog.results,
                                  nuraResultsDetailsDialog.title,
                                )}
                              </Typography>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  marginLeft: '5px',
                                }}
                              >
                                {nuraResultsDetailsDialog.signInitials}
                              </span>
                            </Grid>
                            <Grid item xs={19} sm={9} md={10} lg={9}>
                              <MulticolorSlider
                                result={nuraResultsDetailsDialog.results[0]}
                                metricName={nuraResultsDetailsDialog.title}
                              />
                            </Grid>
                          </>
                        ) : (
                          <Grid
                            item
                            xs={4}
                            sm={6}
                            md={10}
                            lg={11}
                            sx={{
                              padding: '0 !important',
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'top',
                              alignItems: 'center',
                              ml: 1,
                            }}
                          >
                            <MulticolorSlider
                              result={nuraResultsDetailsDialog.results[0]}
                              metricName={nuraResultsDetailsDialog.title}
                            />
                            <Typography
                              variant={isMediumScreen ? 'body2' : 'subtitle1'}
                              sx={{fontWeight: 'bold', ml: 2}}
                            >
                              {handleResultDisplayByMetric(
                                nuraResultsDetailsDialog.results,
                                nuraResultsDetailsDialog.title,
                              )}
                            </Typography>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '5px',
                              }}
                            >
                              {nuraResultsDetailsDialog.signInitials}
                            </span>
                          </Grid>
                        )}
                      </>
                    );
                  })()}
                </Grid>
                <Typography variant="body2" sx={{marginTop: 1}}>
                  {nuraResultsDetailsDialog.details}
                </Typography>
              </DialogContent>
              <DialogActions
                sx={{
                  paddingBottom: theme.spacing(2),
                  justifyContent: 'flex-start',
                }}
              >
                <Button
                  variant="text"
                  onClick={() => handleCloseModal(false)}
                  aria-label="back"
                  startIcon={<ArrowBackIcon />}
                >
                  Go back to results
                </Button>
              </DialogActions>
              <CustomBanner toggleAccordionBanner={toggleAccordion} expanded={expanded} />
            </Dialog>
          ) : null}
          <AdvancedScreeningDialog
            advancedScreeningDialog={advancedScreeningDialog}
            setAdvancedScreeningDialog={setAdvancedScreeningDialog}
          />
        </>
      )}
    </>
  );
};
